import {
  AlertDialogContainer,
  Button,
  Tag,
  FileIngestionResults,
  getFileIngestionErrors,
  getDataType,
  isOsfFile,
  sortConfigFilesBySensorOrder,
  useUploadService,
  BulkUploaderWindow,
  MinimizedUploaderWidget,
  OUSTER_SUPPORTED,
  ResumeUploadsConfirm,
} from '@ouster/webviz-react-components';
import * as Sentry from '@sentry/react';
import type {
  DataSet,
  Drive,
  FileIngestionErrors,
  FileProcessingStatus,
  FileProcessingStatusState,
  SignedUrlResponse,
  UploadDataSet,
  UploadSession,
  UploadStats,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useState, useRef } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import {
  DEFAULT_UPLOAD_CONFIGURATION,
  fetchUploadConfiguration,
} from '../../pages/adminDashboard/uploadConfiguration';
import { useStoreActions, useStoreState } from '../../reducers';
import { BASE_ORG_API } from '../../utils/constants';
import { createDrive, getFilesByUploadId } from '../../utils/createDrives';
import { fetchWithAuth } from '../../utils/requests';
import {
  buildCreatedUploadSessionDetail,
  createInterruptedUploadError,
  createSignedUrlFileUpload,
  buildUploadDriveCreatedDetail,
  type NetworkRecoveryDetail,
  buildRecordingLinksClipboardText,
  buildRecordingLinksCopiedDescription,
  buildSignedUrlUploadResponse,
  canResumeInterruptedUploads,
  cleanupUploadDriveOnError,
  closeUploadSessionWithRetry,
  fetchResumeStatusesWithRetry,
  getInterruptedDurationMs,
  getCompletedRecordingDatasets,
  getResumeStatusChecks,
  isTransientRequestError,
  logResumableUpload,
  normalizeFileUploadStatus,
  pollUploadSessionValidation,
  resolveUploadOrgId,
} from './UploaderContainerHelpers';

type PendingResumePrompt = {
  uploadIds: string[];
  uploadNames: string[];
};

const OFFLINE_STATUS_TOAST_ID = 'bulk-upload-offline-status';
// Shared id so several files recovering at once collapse into one toast rather
// than stacking one per file.
const RESUME_STATUS_TOAST_ID = 'bulk-upload-resume-status';

// Backstop only: the `online` listener and the early-waking backoff handle an
// ordinary reconnect. This exists for the case where neither fires, so it is
// kept short enough that recovery is not visibly delayed when it is the only
// thing left to trigger it. Ticks are cheap - they early-return unless an
// upload is actually sitting interrupted.
const RESUME_RETRY_INTERVAL_MS = 10 * 1000;

type OfflineToastStage = 'idle' | 'warning' | 'error';

type ResumableUploadService = ReturnType<typeof useUploadService> & {
  cancelUpload: (uploadId: string) => void;
  estimateInterruptedUploadsRemainingSeconds: (uploadIds?: string[]) => number;
  getInterruptedUploads: (uploadIds?: string[]) => UploadDataSet[];
  resumeInterruptedUploads: (uploadIds?: string[]) => Promise<void>;
};

export default function UploaderContainer() {
  const { curUserOrg, isOusterUser, user } = useStoreState(
    (storeState) => storeState.user
  );
  // Extracted so callbacks that only need the id can list just that instead
  // of depending on the whole curUserOrg object.
  const curUserOrgId = curUserOrg?.id;
  const { uploadModalStatus } = useStoreState(
    (storeState) => storeState.recordingUpload
  );
  const { dataCategories } = useStoreState((storeState) => ({
    ...storeState.dataCategory,
  }));
  const { tags } = useStoreState((storeState) => ({
    ...storeState.recordingTag,
  }));
  const { orgSpaceUsage } = useStoreState(
    (storeState) => storeState.organization
  );

  const { fetchOrgSpaceUsage } = useStoreActions(
    (actions) => actions.organization
  );
  const { setUploadModalStatus } = useStoreActions(
    (actions) => actions.recordingUpload
  );
  const { loadDataCategories } = useStoreActions(
    (actions) => actions.dataCategory
  );
  const { fetchTags, createTag } = useStoreActions(
    (actions) => actions.recordingTag
  );

  // Lifted state for file management (persists across minimize/restore)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [configFiles, setConfigFiles] = useState<File[]>([]);
  const [dataSets, setDataSets] = useState<DataSet[]>([]);
  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [isRestoringFromMinimized, setIsRestoringFromMinimized] =
    useState(false);

  // Stable refs for curUserOrg and user so upload service callbacks never change
  // identity when the org switches (which would cause useUploadService to re-init
  // and cancel all in-flight uploads)
  const curUserOrgRef = useRef(curUserOrg);
  const userRef = useRef(user);
  useEffect(() => {
    curUserOrgRef.current = curUserOrg;
  }, [curUserOrg]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Maps sessionId -> orgId so callbacks always use the org that created the
  // session, even if the user switches orgs while the upload is in flight
  // so when FAE upload in provisioned organization, they can switch back to their main org without interrupting the upload
  const sessionOrgMapRef = useRef<Map<string, number>>(new Map());

  // Toast hook for offline warnings
  const { closeToast, showToast } = useChakraHook();
  const offlineToastStageRef = useRef<OfflineToastStage>('idle');
  const offlineWarningTimeoutRef = useRef<number | null>(null);
  const offlineErrorTimeoutRef = useRef<number | null>(null);
  const offlineStartedAtRef = useRef<number | null>(null);
  const reconnectOfflineDurationMsRef = useRef<number | null>(null);
  const offlineSessionIdRef = useRef(0);
  const uploadConfigurationRef = useRef(DEFAULT_UPLOAD_CONFIGURATION);
  const reconnectAttemptCountRef = useRef(0);
  const handledReconnectAttemptRef = useRef(0);
  const [pendingResumePrompt, setPendingResumePrompt] =
    useState<PendingResumePrompt | null>(null);
  const [isResumeFailureDialogOpen, setIsResumeFailureDialogOpen] =
    useState(false);
  const [hasDeferredResumeFailureDialog, setHasDeferredResumeFailureDialog] =
    useState(false);
  const [reconnectAttemptCount, setReconnectAttemptCount] = useState(0);
  const resumeHandlingRef = useRef(false);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadDataCategories(curUserOrg.id);
      fetchTags(curUserOrg.id);
    }
  }, [curUserOrg?.id, fetchTags, loadDataCategories]);

  useEffect(() => {
    if (uploadModalStatus === 'ACTIVE_MAX' && curUserOrg?.id) {
      fetchTags(curUserOrg.id);
    }
  }, [curUserOrg?.id, fetchTags, uploadModalStatus]);

  useEffect(() => {
    if (!curUserOrg?.id) {
      return;
    }

    let isActive = true;

    void fetchUploadConfiguration(curUserOrg.id)
      .then((configuration) => {
        if (isActive) {
          uploadConfigurationRef.current = configuration;
        }
      })
      .catch((error) => {
        console.error('Failed to load upload configuration:', error);
      });

    return () => {
      isActive = false;
    };
  }, [curUserOrg?.id]);

  const supportedFiles = OUSTER_SUPPORTED;

  // create upload session, for each dataset upload, create session first
  const onCreateUploadSession = useCallback(
    async (uploadDataSet: UploadDataSet): Promise<UploadSession> => {
      const orgId = curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const dataType = getDataType(uploadDataSet.dataFile.name);
      const uploadUrl = `${BASE_ORG_API}/${orgId}/uploads/`;
      const response = await fetchWithAuth(uploadUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: dataType }),
      });

      if (!response.ok) {
        throw new Error('Failed to create upload session');
      }

      const responseBody = (await response.json()) as {
        uuid: string;
        source_bucket?: string;
      };
      const { sessionId, sourceBucket, uploadSession } =
        buildCreatedUploadSessionDetail(responseBody);

      if (sourceBucket) {
        localStorage.setItem('source_bucket', sourceBucket);
      }

      // Record which org owns this session so later callbacks use the right org
      sessionOrgMapRef.current.set(sessionId, orgId);

      return uploadSession;
    },
    []
  );

  // generate signed URL for file upload
  const onGenerateSignedUrl = useCallback(
    async (fileName: string, sessionId: string): Promise<SignedUrlResponse> => {
      const orgId = resolveUploadOrgId(
        sessionOrgMapRef.current,
        sessionId,
        curUserOrgRef.current?.id
      );

      // Get signed URL for file upload within existing session
      const fileUploadUrl = `${BASE_ORG_API}/${orgId}/uploads/${sessionId}/files/`;
      const signedUrlResponse = await fetchWithAuth(fileUploadUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: fileName }),
      });

      if (!signedUrlResponse.ok) {
        const responseBody = (await signedUrlResponse
          .json()
          .catch(() => null)) as { reason?: unknown } | null;
        const reason = responseBody?.reason;

        throw new Error(
          typeof reason === 'string' && reason.trim()
            ? reason
            : 'Failed to get signed URL'
        );
      }

      const signedUrlBody = (await signedUrlResponse.json()) as {
        signed_url: string;
        uuid: string;
        resumable_url?: string;
        resumable_expires_at?: string;
      };
      return buildSignedUrlUploadResponse(signedUrlBody, sessionId);
    },
    []
  );

  // Return XMLHttpRequest immediately to allow upload cancellation
  // then return a Promise that resolves/rejects based on upload outcome
  /**
   * An upload that rides out a network drop inside its own retry/backoff never
   * reaches the interrupted state, so the resume flow - and the toast it shows -
   * never runs. That is now the common outcome rather than the rare one, and it
   * is the only path available at all when the browser emits no offline/online
   * pair, so the notification has to come from the upload itself.
   */
  const handleUploadNetworkRecovered = useCallback(
    ({ interruptionDurationMs }: NetworkRecoveryDetail) => {
      const offlineToastDelayMs =
        uploadConfigurationRef.current.disruption_grace_period_seconds * 1000;

      // Matches the threshold that governs the offline toasts: a blip too short
      // to have been surfaced as a disruption should not announce a recovery.
      if (interruptionDurationMs < offlineToastDelayMs) {
        logResumableUpload('recovery too short to notify', {
          interruptionDurationMs,
          offlineToastDelayMs,
        });
        return;
      }

      if (offlineToastStageRef.current !== 'idle') {
        closeToast(OFFLINE_STATUS_TOAST_ID);
        offlineToastStageRef.current = 'idle';
      }

      showToast({
        id: RESUME_STATUS_TOAST_ID,
        title: 'Upload resumed',
        description:
          'The connection has been restored and your upload has resumed.',
        status: 'info',
      });
    },
    [closeToast, showToast]
  );

  const onUploadFileToSignedUrl = useCallback(
    (
      file: File,
      uploadTarget: SignedUrlResponse,
      onProgress: (progress: number) => void
    ): { request: { abort: () => void }; promise: Promise<void> } => {
      return createSignedUrlFileUpload({
        file,
        uploadTarget,
        onProgress,
        onNetworkRecovered: handleUploadNetworkRecovered,
        createXhr: () => new XMLHttpRequest(),
      });
    },
    [handleUploadNetworkRecovered]
  );

  // poll file upload status
  const onPollFileUploadStatus = useCallback(
    async (
      sessionId: string,
      fileUploadId: string,
      fileType?: 'data' | 'config'
    ): Promise<FileProcessingStatus> => {
      const orgId = resolveUploadOrgId(
        sessionOrgMapRef.current,
        sessionId,
        curUserOrgRef.current?.id
      );

      const statusUrl = `${BASE_ORG_API}/${orgId}/uploads/${sessionId}/files/${fileUploadId}`;
      const response = await fetchWithAuth(statusUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        const statusError = new Error(
          'Failed to get upload status'
        ) as Error & {
          status: number;
        };
        statusError.status = response.status;
        throw statusError;
      }

      const statusBody = (await response.json()) as {
        state?: FileProcessingStatusState;
        ingestion_result?: FileIngestionResults;
        errors?: FileIngestionErrors;
        resumable_expired?: boolean;
      };

      return normalizeFileUploadStatus(
        statusBody,
        fileUploadId,
        sessionId,
        fileType,
        (ingestionErrors: FileIngestionErrors) => {
          const [warnings, fatalErrors] =
            getFileIngestionErrors(ingestionErrors);
          return [warnings, fatalErrors];
        }
      );
    },
    []
  );

  const onCreateRecordingFromSession = useCallback(
    async (uploadDataSet: UploadDataSet): Promise<Drive> => {
      const orgId = uploadDataSet.uploadSessionId
        ? sessionOrgMapRef.current.get(uploadDataSet.uploadSessionId) ??
          curUserOrgRef.current?.id
        : curUserOrgRef.current?.id;
      if (!orgId) {
        throw new Error('No organization found');
      }

      try {
        // Step 1: Prepare config file results
        let configInfo = uploadDataSet.ingestionResults;

        // For OSF uploads, get all config files from the session (multi-sensor case)
        // Check if it's an OSF upload by looking at the data file extension
        const isOsfUpload = isOsfFile(uploadDataSet.dataFile.name);
        if (isOsfUpload && uploadDataSet.uploadSessionId) {
          configInfo = await getFilesByUploadId(
            uploadDataSet.uploadSessionId,
            orgId
          );
        }
        // Step 2: Sort sensor info by product serial number using utility
        if (configInfo && configInfo.length > 0) {
          configInfo = sortConfigFilesBySensorOrder(configInfo);
        }

        // Step 3: Create drive using sorted config info
        const driveResponse = await createDrive(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          uploadDataSet.tags || [],
          orgId,
          uploadDataSet.uploadSessionId,
          uploadDataSet.dataCategory,
          configInfo
        );

        if (!driveResponse?.ok) {
          const driveError = new Error(
            `Failed to create drive for session ${uploadDataSet.uploadSessionId}`
          ) as Error & { status?: number };
          driveError.status = driveResponse?.status;
          throw driveError;
        }

        const driveData = (await driveResponse.json()) as { drive_id: string };

        // Store drive_id for cleanup on validation error
        if (driveData.drive_id) {
          localStorage.setItem('drive_id', driveData.drive_id.toString());
        }

        let isFinalizedDriveVerified = false;
        const verifyFinalizedDrive = async () => {
          if (isFinalizedDriveVerified) {
            return;
          }

          if (!uploadDataSet.uploadSessionId) {
            throw new Error('No session ID available for drive verification');
          }

          const sourcePrefix = `final/${uploadDataSet.uploadSessionId}/`;
          const driveExistsResponse = await fetchWithAuth(
            `${BASE_ORG_API}/${orgId}/recordings/exists?source_prefix=${encodeURIComponent(
              sourcePrefix
            )}`,
            { method: 'GET' }
          );

          if (!driveExistsResponse.ok) {
            const verificationError = new Error(
              `Failed to verify finalized drive: ${driveExistsResponse.status}`
            ) as Error & { status: number };
            verificationError.status = driveExistsResponse.status;
            throw verificationError;
          }

          type DriveExistsResult = {
            exists?: boolean;
            drive_id?: number;
          };
          const responseBody = (await driveExistsResponse.json()) as
            | DriveExistsResult
            | { data?: DriveExistsResult };
          const driveExistsResult: DriveExistsResult | undefined =
            'data' in responseBody
              ? responseBody.data
              : (responseBody as DriveExistsResult);

          if (
            !driveExistsResult?.exists ||
            Number(driveExistsResult.drive_id) !== Number(driveData.drive_id)
          ) {
            throw new Error(
              'Upload session was not found and the finalized drive could not be verified'
            );
          }

          isFinalizedDriveVerified = true;
        };

        // Step 4: Close the upload session with retry logic
        const closeSessionUrl = `${BASE_ORG_API}/${orgId}/uploads/${uploadDataSet.uploadSessionId}`;

        const retryDelays = [1000, 3000, 5000]; // 1s, 3s, 5s
        await closeUploadSessionWithRetry({
          closeSession: async () => {
            const closeResponse = await fetchWithAuth(closeSessionUrl, {
              method: 'PATCH',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                state: 'closed',
                attributes: {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                  tags: (uploadDataSet.tags || []).map((tag) => tag.id),
                  data_category: uploadDataSet.dataCategory?.id,
                  user: userRef.current?.name || 'John Doe',
                },
              }),
            });

            if (closeResponse.status === 404) {
              await verifyFinalizedDrive();
              return {
                ok: true,
                status: 404,
                statusText: closeResponse.statusText,
              };
            }

            return closeResponse;
          },
          retryDelays,
          onRetry: (attempt, delayMs) => {
            console.warn(
              `Attempt ${attempt} to close session failed. Retrying in ${delayMs}ms...`
            );
          },
          wait: (delayMs: number) =>
            new Promise((resolve) => {
              window.setTimeout(() => {
                resolve();
              }, delayMs);
            }),
        });

        // Step 5: Poll session until validation completes using utility
        try {
          if (!uploadDataSet.uploadSessionId) {
            throw new Error('No session ID available for validation');
          }
          await pollUploadSessionValidation({
            sessionId: uploadDataSet.uploadSessionId,
            orgId: orgId.toString(),
            fetchSessionStatus: async (sessionId, uploadOrgId) => {
              const validationResponse = await fetchWithAuth(
                `${BASE_ORG_API}/${uploadOrgId}/uploads/${sessionId}`,
                {
                  method: 'GET',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                  },
                }
              );

              if (validationResponse.status === 404) {
                await verifyFinalizedDrive();
                return { state: 'closed' };
              }

              if (!validationResponse.ok) {
                const validationError = new Error(
                  `Failed to get upload validation status: ${validationResponse.status}`
                ) as Error & { status: number };
                validationError.status = validationResponse.status;
                throw validationError;
              }

              return (await validationResponse.json()) as { state?: string };
            },
            wait: (delayMs: number) =>
              new Promise((resolve) => {
                window.setTimeout(() => {
                  resolve();
                }, delayMs);
              }),
          });
          localStorage.removeItem('drive_id'); // Clean up on success
          // Session complete — remove from map
          if (uploadDataSet.uploadSessionId) {
            sessionOrgMapRef.current.delete(uploadDataSet.uploadSessionId);
          }

          // Dispatch event with drive metadata for optimistic UI insertion
          window.dispatchEvent(
            new CustomEvent('upload-drive-created', {
              detail: buildUploadDriveCreatedDetail({
                driveId: Number(driveData.drive_id),
                orgId,
                uploadDataSet,
                uploadedBy: userRef.current?.name ?? null,
                processedAt: new Date(),
                configInfo,
              }),
            })
          );

          return {
            drive_id: Number(driveData.drive_id),
            status: 'created',
          };
        } catch (error) {
          if (isTransientRequestError(error)) {
            throw error;
          }

          // Clean up drive on validation failure
          if (driveData.drive_id) {
            await cleanupUploadDriveOnError({
              driveId: driveData.drive_id,
              orgId: orgId.toString(),
              deleteDrive: async (cleanupDriveId, cleanupOrgId) => {
                const deleteUrl = `${BASE_ORG_API}/${cleanupOrgId}/recordings/${cleanupDriveId}`;
                await fetchWithAuth(deleteUrl, { method: 'DELETE' });
              },
              clearDriveId: () => {
                localStorage.removeItem('drive_id');
              },
              onCleanupError: (cleanupError, detail) => {
                console.error(
                  'Failed to clean up drive after error:',
                  cleanupError
                );
                Sentry.captureException(cleanupError, {
                  tags: {
                    section: 'UploaderContainer',
                    thunk: 'cleanupDriveOnError',
                  },
                  extra: detail,
                });
              },
            });
          }
          throw error;
        }
      } catch (error) {
        if (isTransientRequestError(error)) {
          throw createInterruptedUploadError(
            error instanceof Error
              ? error.message
              : 'Upload validation interrupted'
          );
        }

        // Clean up drive on any error
        const driveId = localStorage.getItem('drive_id');
        if (driveId) {
          await cleanupUploadDriveOnError({
            driveId,
            orgId: orgId.toString(),
            deleteDrive: async (cleanupDriveId, cleanupOrgId) => {
              const deleteUrl = `${BASE_ORG_API}/${cleanupOrgId}/recordings/${cleanupDriveId}`;
              await fetchWithAuth(deleteUrl, { method: 'DELETE' });
            },
            clearDriveId: () => {
              localStorage.removeItem('drive_id');
            },
            onCleanupError: (cleanupError, detail) => {
              console.error(
                'Failed to clean up drive after error:',
                cleanupError
              );
              Sentry.captureException(cleanupError, {
                tags: {
                  section: 'UploaderContainer',
                  thunk: 'cleanupDriveOnError',
                },
                extra: detail,
              });
            },
          });
        }
        // Remove session from map on error too
        if (uploadDataSet.uploadSessionId) {
          sessionOrgMapRef.current.delete(uploadDataSet.uploadSessionId);
        }

        throw error instanceof Error
          ? error
          : new Error('Failed to create recording from session');
      }
    },
    []
  );

  // Additional callback handlers for upload events
  const onUploadSuccess = useCallback(() => {
    const orgId = curUserOrgRef.current?.id;
    if (orgId) {
      fetchOrgSpaceUsage(orgId);
    }
  }, [fetchOrgSpaceUsage]);

  const onUploadError = useCallback(
    (
      error: Error,
      context: { datasetId: string; sessionId?: string; fileId?: string }
    ) => {
      console.error('Upload error:', error, context);
      // Central funnel for every stage of the upload pipeline (session
      // creation, signed URL generation, the file PUT itself, status
      // polling, recording creation) - each failure is a distinct,
      // actionable occurrence (a specific file/dataset), so report every
      // one rather than deduping.
      Sentry.captureException(error, {
        tags: { section: 'UploaderContainer', thunk: 'onUploadError' },
        extra: context,
      });
    },
    []
  );

  // Add cleanup callback to handle file deletion
  const onCleanupUploadFiles = useCallback(
    (
      uploadSessionId: string,
      dataUploadId?: string,
      configUploadIds?: string[]
    ) => {
      const orgId =
        sessionOrgMapRef.current.get(uploadSessionId) ??
        curUserOrgRef.current?.id;
      if (!orgId) return;

      // Delete data file if it exists
      if (dataUploadId) {
        const dataDeleteURL = `${BASE_ORG_API}/${orgId}/uploads/${uploadSessionId}/files/${dataUploadId}`;
        fetchWithAuth(dataDeleteURL, {
          method: 'DELETE',
        }).catch((error) => {
          console.warn(`Failed to delete data file ${dataUploadId}:`, error);
        });
      }

      // Delete config files if they exist
      if (configUploadIds && Array.isArray(configUploadIds)) {
        configUploadIds.forEach((id) => {
          const configDeleteURL = `${BASE_ORG_API}/${orgId}/uploads/${uploadSessionId}/files/${id}`;
          fetchWithAuth(configDeleteURL, {
            method: 'DELETE',
          }).catch((error) => {
            console.warn(`Failed to delete config file ${id}:`, error);
          });
        });
      }
    },
    []
  );

  const onCreateTag = useCallback(
    async (name: string) => {
      if (!curUserOrgId)
        return Promise.reject(new Error('No organization found'));
      try {
        const newTag = (await createTag({
          organization_id: curUserOrgId,
          name,
        })) as Tag;
        return newTag;
      } catch (error) {
        console.error('Error creating tag:', error);
        return Promise.reject(
          error instanceof Error ? error : new Error(String(error))
        );
      }
    },
    [createTag, curUserOrgId]
  );

  const uploadService = useUploadService({
    onCreateUploadSession,
    onGenerateSignedUrl,
    onUploadFileToSignedUrl,
    onPollFileUploadStatus,
    onCreateRecordingFromSession,
    onUploadSuccess,
    onUploadError,
    onCleanupUploadFiles,
    maxConcurrentUploads: 3,
  }) as ResumableUploadService;

  // implement wakeLock here to prevent system sleep during active upload
  const { uploadingCount, processingCount, validatingCount } =
    uploadService.uploadStats as UploadStats;

  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);

  const activeCount = uploadingCount + processingCount + validatingCount;

  // Request wake lock when uploads are active
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator && !wakeLock) {
      try {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);

        // Handle release events
        lock.addEventListener('release', () => {
          setWakeLock(null);
        });
      } catch {
        // Wake lock request may fail if page is not visible or focused
        // This is expected behavior and not an error we need to log
        // The uploads will continue normally without preventing screen sleep
      }
    }
  }, [wakeLock]);

  // Release wake lock
  const releaseWakeLock = useCallback(() => {
    if (wakeLock) {
      wakeLock.release();
      setWakeLock(null);
    }
  }, [wakeLock]);

  // Clear uploader state when organization changes
  useEffect(() => {
    const exitFromProvisioning =
      sessionStorage.getItem('exitFromProvisioning') === 'true';
    const uploadsActive = sessionStorage.getItem('uploadsActive');

    if (exitFromProvisioning) {
      if (uploadsActive) {
        // Defer showToast past the current render/navigation flush so the
        // Chakra toast scheduler isn't preempted by the org-change re-render
        setTimeout(() => {
          showToast({
            title: 'Exit from provisioning mode',
            description: `Your upload will continue in the background.`,
            status: 'info',
          });
        }, 0);
      }

      sessionStorage.removeItem('exitFromProvisioning');
      return; // Don't clear state on initial load when exiting provisioning manager
    }

    // selectedFiles/configFiles/dataSets accumulate from user file picks and
    // in-flight uploads across renders — not derivable from curUserOrg?.id —
    // this effect only clears that sticky local queue when the org actually
    // switches (and after checking the external sessionStorage flag above).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedFiles([]);
    setConfigFiles([]);
    setDataSets([]);
    uploadService.clearAllUploads();
  }, [curUserOrg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage wake lock based on active upload count and page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeCount > 0) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check
    if (activeCount > 0) {
      // Synchronizes with the external Wake Lock browser API (requests an
      // actual screen wake lock); the setState inside requestWakeLock just
      // records the resulting sentinel, it isn't derivable from render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeCount, requestWakeLock, releaseWakeLock]); // Release wake lock when component unmounts
  useEffect(() => () => releaseWakeLock(), [releaseWakeLock]);

  // Persist whether uploads are active so other UI (e.g. org switcher) can check
  useEffect(() => {
    try {
      if (activeCount > 0) {
        sessionStorage.setItem('uploadsActive', 'true');
      } else {
        sessionStorage.removeItem('uploadsActive');
      }
    } catch {
      // sessionStorage may throw in some environments; ignore safely
    }
  }, [activeCount]);

  const clearOfflineToastTimers = useCallback(() => {
    if (offlineWarningTimeoutRef.current !== null) {
      window.clearTimeout(offlineWarningTimeoutRef.current);
      offlineWarningTimeoutRef.current = null;
    }

    if (offlineErrorTimeoutRef.current !== null) {
      window.clearTimeout(offlineErrorTimeoutRef.current);
      offlineErrorTimeoutRef.current = null;
    }
  }, []);

  // Monitor offline status during active uploads
  useEffect(() => {
    const handleOnline = () => {
      const offlineStartedAtMs = offlineStartedAtRef.current;
      reconnectOfflineDurationMsRef.current =
        typeof offlineStartedAtMs === 'number'
          ? Math.max(Date.now() - offlineStartedAtMs, 0)
          : null;
      offlineStartedAtRef.current = null;
      offlineSessionIdRef.current += 1;
      clearOfflineToastTimers();

      if (offlineToastStageRef.current !== 'idle') {
        closeToast(OFFLINE_STATUS_TOAST_ID);
      }

      offlineToastStageRef.current = 'idle';
      reconnectAttemptCountRef.current += 1;
      setReconnectAttemptCount(reconnectAttemptCountRef.current);
    };

    const handleOffline = () => {
      try {
        const uploadsActive =
          sessionStorage.getItem('uploadsActive') === 'true';
        if (!uploadsActive) {
          return;
        }

        if (
          offlineWarningTimeoutRef.current !== null ||
          offlineErrorTimeoutRef.current !== null
        ) {
          return;
        }

        offlineStartedAtRef.current = Date.now();
        reconnectOfflineDurationMsRef.current = null;
        offlineSessionIdRef.current += 1;
        const offlineSessionId = offlineSessionIdRef.current;
        const offlineToastDelayMs =
          uploadConfigurationRef.current.disruption_grace_period_seconds * 1000;
        const longDisconnectThresholdMs =
          uploadConfigurationRef.current.active_retry_window_seconds * 1000;

        offlineWarningTimeoutRef.current = window.setTimeout(() => {
          if (offlineSessionIdRef.current !== offlineSessionId) {
            return;
          }

          offlineWarningTimeoutRef.current = null;
          offlineToastStageRef.current = 'warning';
          showToast({
            id: OFFLINE_STATUS_TOAST_ID,
            title: 'Connection interrupted',
            description:
              'Active uploads will automatically resume once the connection is restored.',
            status: 'warning',
            isStay: true,
          });
        }, offlineToastDelayMs);

        offlineErrorTimeoutRef.current = window.setTimeout(() => {
          if (offlineSessionIdRef.current !== offlineSessionId) {
            return;
          }

          offlineErrorTimeoutRef.current = null;
          offlineToastStageRef.current = 'error';
          showToast({
            id: OFFLINE_STATUS_TOAST_ID,
            title: 'Connection lost',
            description:
              'Your device is offline. The active upload progress has been saved. Once the connection is restored, you can review and resume your uploads.',
            status: 'error',
            isStay: true,
          });
        }, longDisconnectThresholdMs);
      } catch (error) {
        console.error('Error handling offline event:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearOfflineToastTimers();
    };
  }, [clearOfflineToastTimers, closeToast, showToast]);

  const cancelInterruptedUploads = useCallback(
    (uploadIds: string[]) => {
      uploadIds.forEach((uploadId) => {
        uploadService.cancelUpload(uploadId);
      });
    },
    [uploadService]
  );

  const handleInterruptedUploadsReconnect = useCallback(
    async ({
      requireConfirmation,
      showResumeToast,
      uploadIds,
    }: {
      requireConfirmation?: boolean;
      showResumeToast?: boolean;
      uploadIds?: string[];
    }): Promise<boolean> => {
      if (resumeHandlingRef.current) {
        return false;
      }

      resumeHandlingRef.current = true;

      try {
        const resumableUploads = uploadService.getInterruptedUploads(uploadIds);

        if (resumableUploads.length === 0) {
          setPendingResumePrompt(null);
          return true;
        }

        const interruptionDurationMs =
          getInterruptedDurationMs(resumableUploads);
        const reconnectOfflineDurationMs =
          reconnectOfflineDurationMsRef.current;
        // Left in place until this attempt succeeds. Consuming it up front meant
        // a failed attempt discarded the measured offline duration, so the retry
        // fell back to a shorter estimate and misjudged how to notify the user.
        const disconnectDurationMs =
          reconnectOfflineDurationMs ?? interruptionDurationMs;
        const offlineToastDelayMs =
          uploadConfigurationRef.current.disruption_grace_period_seconds * 1000;
        const longDisconnectThresholdMs =
          uploadConfigurationRef.current.active_retry_window_seconds * 1000;
        const shouldRequireConfirmation =
          requireConfirmation ??
          disconnectDurationMs >= longDisconnectThresholdMs;
        const shouldShowResumeToast =
          showResumeToast ??
          (disconnectDurationMs >= offlineToastDelayMs &&
            disconnectDurationMs < longDisconnectThresholdMs);
        closeToast(OFFLINE_STATUS_TOAST_ID);
        const resumeStatusChecks = getResumeStatusChecks(resumableUploads);
        logResumableUpload('resume attempt started', {
          uploadCount: resumableUploads.length,
          statusCheckCount: resumeStatusChecks.length,
          disconnectDurationMs,
          navigatorOnLine: navigator.onLine,
        });
        const resumeStatuses = await fetchResumeStatusesWithRetry({
          checks: resumeStatusChecks,
          pollFileUploadStatus: onPollFileUploadStatus,
        });
        // The status check is the only step that can fail, so the measured
        // offline duration is safe to consume now that it has resolved.
        reconnectOfflineDurationMsRef.current = null;

        const nowMs = Date.now();
        const resumeDecisionData = resumableUploads.map((upload) => {
          const estimatedRemainingSeconds =
            uploadService.estimateInterruptedUploadsRemainingSeconds([
              upload.id,
            ]);
          const uploadStatuses = resumeStatuses.filter(
            (status) =>
              status.session_id === upload.uploadSessionId &&
              (status.file_id === upload.dataUploadId ||
                upload.configUploadIds?.includes(status.file_id))
          );
          const hasUploadExpiredResume = uploadStatuses.some(
            (status) => status.resumable_expired
          );
          const hasUploadSufficientResumeWindow = canResumeInterruptedUploads({
            estimatedRemainingSeconds,
            nowMs,
            uploads: [upload],
          });

          return {
            uploadId: upload.id,
            uploadSessionId: upload.uploadSessionId,
            dataFileName: upload.dataFile.name,
            interruptedAt: upload.interruptedAt,
            dataUploadProgress: upload.dataUploadProgress,
            configUploadProgress: upload.configUploadProgress,
            estimatedRemainingSeconds,
            dataResumableExpiresAt:
              upload.dataUploadTarget?.resumable_expires_at,
            configResumableExpiresAt:
              upload.configUploadTarget?.resumable_expires_at,
            hasExpiredResume: hasUploadExpiredResume,
            hasSufficientResumeWindow: hasUploadSufficientResumeWindow,
            polledStatuses: uploadStatuses.map((status) => ({
              fileId: status.file_id,
              fileType: status.file_type,
              state: status.state,
              resumableExpired: status.resumable_expired,
            })),
          };
        });

        const blockedUploads = resumeDecisionData.filter(
          (upload) =>
            upload.hasExpiredResume || !upload.hasSufficientResumeWindow
        );
        const uploadsToResume = resumeDecisionData.filter(
          (upload) =>
            !upload.hasExpiredResume && upload.hasSufficientResumeWindow
        );
        const blockedUploadIds = blockedUploads.map(
          (upload) => upload.uploadId
        );
        const resumableUploadIds = uploadsToResume.map(
          (upload) => upload.uploadId
        );

        if (blockedUploads.length > 0) {
          cancelInterruptedUploads(blockedUploadIds);
        }

        if (uploadsToResume.length === 0) {
          setPendingResumePrompt(null);
          setHasDeferredResumeFailureDialog(false);
          if (blockedUploads.length > 0) {
            setIsResumeFailureDialogOpen(true);
          }
          return true;
        }

        if (shouldRequireConfirmation) {
          setHasDeferredResumeFailureDialog(blockedUploads.length > 0);
          setPendingResumePrompt({
            uploadIds: resumableUploadIds,
            uploadNames: uploadsToResume.map((upload) => upload.dataFileName),
          });
          return true;
        }

        setHasDeferredResumeFailureDialog(false);
        setPendingResumePrompt(null);
        void uploadService.resumeInterruptedUploads(resumableUploadIds);
        if (blockedUploads.length > 0) {
          setIsResumeFailureDialogOpen(true);
        }
        if (shouldShowResumeToast) {
          const resumedUploadCount = resumableUploadIds.length;
          const resumedUploadLabel =
            resumedUploadCount === 1 ? 'Upload' : 'Uploads';
          const resumedUploadDescription =
            resumedUploadCount === 1
              ? 'The connection has been restored and your upload has resumed.'
              : 'The connection has been restored and your uploads have resumed.';
          showToast({
            title: `${resumedUploadLabel} resumed`,
            description: resumedUploadDescription,
            status: 'info',
          });
        }

        logResumableUpload('resume attempt succeeded', {
          resumedCount: resumableUploadIds.length,
          blockedCount: blockedUploads.length,
          disconnectDurationMs,
          shownResumeToast: shouldShowResumeToast,
        });

        return true;
      } catch (error) {
        // The uploads stay in the interrupted state, so the reconnect effect and
        // the periodic retry below can pick them up again on a later attempt.
        logResumableUpload('resume attempt failed', {
          navigatorOnLine: navigator.onLine,
          message: error instanceof Error ? error.message : String(error),
        });
        Sentry.captureException(error, {
          tags: { feature: 'bulk-upload', phase: 'resume-reconnect' },
          extra: { navigatorOnLine: navigator.onLine },
        });

        return false;
      } finally {
        resumeHandlingRef.current = false;
      }
    },
    [
      cancelInterruptedUploads,
      closeToast,
      onPollFileUploadStatus,
      showToast,
      uploadService,
    ]
  );

  useEffect(() => {
    if (
      !navigator.onLine ||
      reconnectAttemptCount === 0 ||
      handledReconnectAttemptRef.current >= reconnectAttemptCount
    ) {
      return;
    }

    if (pendingResumePrompt || isResumeFailureDialogOpen) {
      return;
    }

    // Deliberately no fixed settle delay before the first attempt. `online` does
    // fire before a VPN or corporate proxy has finished renegotiating, but a
    // fixed pause is both too long for an ordinary reconnect and too short for a
    // 30s negotiation; the backoff in fetchResumeStatusesWithRetry covers it.
    const reconnectTimeout = window.setTimeout(() => {
      void handleInterruptedUploadsReconnect({}).then((didHandle) => {
        // Only consume the attempt once it actually succeeded. A failed resume
        // used to mark itself handled and was never retried, so a reconnect that
        // landed in a VPN or proxy settling window abandoned the upload.
        if (didHandle) {
          handledReconnectAttemptRef.current = Math.max(
            handledReconnectAttemptRef.current,
            reconnectAttemptCount
          );
        }
      });
    }, 0);

    return () => {
      window.clearTimeout(reconnectTimeout);
    };
  }, [
    handleInterruptedUploadsReconnect,
    isResumeFailureDialogOpen,
    pendingResumePrompt,
    reconnectAttemptCount,
  ]);

  // The `online` event is not a reliable resume trigger on corporate networks:
  // when a VPN tunnel drops while the Wi-Fi interface stays up, navigator.onLine
  // never flips and no offline/online pair is ever emitted. Poll for interrupted
  // uploads so recovery does not depend on that event firing.
  useEffect(() => {
    const retryInterval = window.setInterval(() => {
      if (!navigator.onLine || resumeHandlingRef.current) {
        return;
      }

      if (pendingResumePrompt || isResumeFailureDialogOpen) {
        return;
      }

      if (uploadService.getInterruptedUploads().length === 0) {
        return;
      }

      logResumableUpload('periodic resume retry tick', {
        navigatorOnLine: navigator.onLine,
      });
      void handleInterruptedUploadsReconnect({});
    }, RESUME_RETRY_INTERVAL_MS);

    return () => {
      window.clearInterval(retryInterval);
    };
  }, [
    handleInterruptedUploadsReconnect,
    isResumeFailureDialogOpen,
    pendingResumePrompt,
    uploadService,
  ]);

  // Listen for a cross-window/component cancel event so the org switcher can request cancellations
  useEffect(() => {
    const handleCancelAll = () => {
      try {
        uploadService.cancelAllUploads();
        // update persisted flag
        sessionStorage.removeItem('uploadsActive');
      } catch (error) {
        console.error('Failed to cancel uploads from external request', error);
      }
    };

    window.addEventListener('cancelAllUploads', handleCancelAll);
    return () => {
      window.removeEventListener('cancelAllUploads', handleCancelAll);
    };
  }, [uploadService]);

  const closeUploaderHandler = () => {
    // clean state from fileManagement
    setSelectedFiles([]);
    setConfigFiles([]);
    setDataSets([]);
    setHasDeferredResumeFailureDialog(false);

    // clean state from uploaderService
    uploadService.clearAllUploads();

    // Only clear uploads if no active uploads
    setUploadModalStatus('INACTIVE');
  };

  const restoreUploaderHandler = () => {
    const uploadStats = uploadService.uploadStats as UploadStats;
    if (uploadStats.totalCount !== 0) {
      setIsRestoringFromMinimized(true);
    }
    setUploadModalStatus('ACTIVE_MAX');
  };

  const minimizeUploaderHandler = () => {
    setUploadModalStatus('ACTIVE_MIN');
  };

  const openVizLink = useCallback(
    (driveId: number) => {
      if (!curUserOrgId) return;

      const url = `/organization/${curUserOrgId}/drive/${driveId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [curUserOrgId]
  );

  const copyRecordingLinks = useCallback(() => {
    if (!curUserOrgId) {
      showToast({
        title: 'Unable to copy links',
        description: 'No organization found.',
        status: 'warning',
      });
      return;
    }

    const completedRecordings = getCompletedRecordingDatasets(
      uploadService.uploadQueue.completed
    );

    if (completedRecordings.length === 0) {
      showToast({
        title: 'No completed recordings',
        description: 'Complete an upload to copy recording links.',
        status: 'info',
      });
      return;
    }

    const clipboardText = buildRecordingLinksClipboardText(
      completedRecordings,
      curUserOrgId,
      window.location.origin
    );

    navigator.clipboard
      .writeText(clipboardText)
      .then(() => {
        showToast({
          title: 'Recording links copied',
          description: buildRecordingLinksCopiedDescription(
            completedRecordings.length
          ),
          status: 'success',
        });
      })
      .catch((error) => {
        console.error('Failed to copy recording links:', error);
        showToast({
          title: 'Copy failed',
          description: 'Unable to copy recording links to clipboard.',
          status: 'error',
        });
      });
  }, [curUserOrgId, showToast, uploadService.uploadQueue.completed]);

  // Reset restoring state when modal becomes active
  useEffect(() => {
    if (uploadModalStatus === 'ACTIVE_MAX') {
      // isRestoringFromMinimized is a one-shot sticky flag set by
      // restoreUploaderHandler to pick the correct defaultActiveTab on the
      // render right after restoring; it must bounce back to false here so
      // it doesn't stay stuck true, which isn't derivable from render alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsRestoringFromMinimized(false);
    }
  }, [uploadModalStatus]);

  if (!curUserOrg) {
    return null;
  }

  return (
    <>
      {uploadModalStatus === 'ACTIVE_MAX' && (
        <BulkUploaderWindow
          orgName={curUserOrg.name}
          orgSpaceUsage={orgSpaceUsage}
          supportedFiles={supportedFiles}
          dataCategories={dataCategories ?? []}
          tags={tags}
          isOusterUser={isOusterUser}
          activeCount={activeCount}
          onCreateTag={onCreateTag}
          onMinimize={minimizeUploaderHandler}
          onClose={closeUploaderHandler}
          openVizLink={openVizLink}
          copyRecordingLinks={copyRecordingLinks}
          defaultActiveTab={isRestoringFromMinimized ? 1 : 0}
          // Pass the upload service instance
          uploadService={uploadService}
          // Pass lifted state
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          configFiles={configFiles}
          setConfigFiles={setConfigFiles}
          dataSets={dataSets}
          setDataSets={setDataSets}
          isEnqueuing={isEnqueuing}
          setIsEnqueuing={setIsEnqueuing}
        />
      )}
      {uploadModalStatus === 'ACTIVE_MIN' && (
        <MinimizedUploaderWidget
          onRestore={restoreUploaderHandler}
          uploadStats={uploadService.uploadStats}
        />
      )}
      {pendingResumePrompt && (
        <ResumeUploadsConfirm
          isOpen
          onClose={() => {
            cancelInterruptedUploads(pendingResumePrompt.uploadIds);
            setPendingResumePrompt(null);
            if (hasDeferredResumeFailureDialog) {
              setHasDeferredResumeFailureDialog(false);
              setIsResumeFailureDialogOpen(true);
            }
          }}
          onCancelUploads={() => {
            cancelInterruptedUploads(pendingResumePrompt.uploadIds);
            setPendingResumePrompt(null);
            if (hasDeferredResumeFailureDialog) {
              setHasDeferredResumeFailureDialog(false);
              setIsResumeFailureDialogOpen(true);
            }
          }}
          onResume={() => {
            const uploadIds = pendingResumePrompt.uploadIds;
            setPendingResumePrompt(null);
            if (hasDeferredResumeFailureDialog) {
              setHasDeferredResumeFailureDialog(false);
              setIsResumeFailureDialogOpen(true);
            }
            void handleInterruptedUploadsReconnect({
              requireConfirmation: false,
              showResumeToast: false,
              uploadIds,
            });
          }}
          uploadNames={pendingResumePrompt.uploadNames}
        />
      )}
      <AlertDialogContainer
        isOpen={isResumeFailureDialogOpen}
        onClose={() => {
          setIsResumeFailureDialogOpen(false);
        }}
        header="We could not resume your uploads"
        content={
          <p style={{ lineHeight: '1.6rem' }}>
            The resumable upload window expired before the remaining data could
            finish uploading. Add the files again to start a new upload.
          </p>
        }
        hideCancelButton
        confirmButton={
          <Button
            onClick={() => {
              setIsResumeFailureDialogOpen(false);
            }}
            variant="outline"
            colorScheme="brand"
          >
            OK
          </Button>
        }
      />
    </>
  );
}
